import CursorPlugin from "wavesurfer.js/dist/plugin/wavesurfer.cursor.min.js"
import WaveSurfer from "wavesurfer.js"
import { onMounted, ref } from "vue"
import { themeVar } from "./helpers";

const WaveformDefaults = {
    height: 70,
    cursorColor: "#66CDA3",
    barWidth: 2,
    barGap: 3,
    barRadius: 0,
    barMinHeight: 1,
    cursorWidth: 2,
}

export function useWaveform(
    container,
    settings = {}
) {
    settings = {...WaveformDefaults, ...settings}

    settings.waveColor = themeVar('waveform-wave')
    settings.progressColor = themeVar('primary')

    const wavesurfer = ref(null)
    const duration = ref(null)
    const currentTime = ref(0)
    const wave = ref(null)
    const progressCanvas = ref(null)
    const isPlaying = ref(false)
    const isMuted = ref(false)

    const play = (start, end) => {
        wavesurfer.value.play(start, end)
    }

    const pause = () => {
        wavesurfer.value.pause()
    }

    const playPause = () => {
        wavesurfer.value.playPause()
    }

    const mute = () => {
        wavesurfer.value.setMute(true)

        isMuted.value = true
    }

    const unmute = () => {
        wavesurfer.value.setMute(false)

        isMuted.value = false
    }

    const skip = (seconds) => {
        wavesurfer.value.skip(seconds)
    }

    const skipBackward = (seconds) => {
        wavesurfer.value.skip(-seconds)
    }

    const drawImmutableRegion = (start, end, clearRegions = false) => {
        if (clearRegions) {
            wavesurfer.value.clearRegions()
        }

        const region = wavesurfer.value.addRegion({
            start: start,
            end: end,
            color: "transparent",
            loop: true,
            resize: false,
            drag: false,
        })

        region.immutable = true

        return region
    }

    const loadAudio = (url, peak = {}, loadDuration) => {
        duration.value = loadDuration

        wavesurfer.value.load(url, peak, 'none', loadDuration)
    }

    const drawRegion = (start, end, clearRegions = false) => {
        if (clearRegions) {
            wavesurfer.value.clearRegions()
        }

        return wavesurfer.value.addRegion({
            start: start,
            end: end,
            color: "transparent",
            loop: false,
            resize: true,
            drag: true,
        })
    }

    onMounted(() => {
        wavesurfer.value = WaveSurfer.create({
            container: container.value,
            backend: "MediaElement",
            fillParent: true,
            scrollParent: false,
            hideScrollbar: true,
            responsive: true,
            preload: false,
            plugins: [
                CursorPlugin.create({
                    showTime: true,
                    opacity: 1,
                    color: '#ffffff',
                    width: '2px',
                    formatTimeCallback: (time) => {
                        return formatTime(time);
                    },
                    customShowTimeStyle: {
                        'background-color': '#000',
                        color: '#fff',
                        padding: '2px',
                        marginLeft: '4px',
                        'font-size': '10px',
                        position: 'absolute',
                        bottom: '4px'
                    }
                }),
            ],
            ...settings,
        })

        wavesurfer.value.on("ready", () => {
            if (!wavesurfer.value.loaded) {
                wavesurfer.value.loaded = true;
            }

            duration.value = wavesurfer.value.getDuration()

            wavesurfer.value.zoom(settings.zoomBase / duration.value)

            wave.value = wavesurfer.value.container.querySelector("wave")
            progressCanvas.value = wave.value.querySelector("wave")
        })

        wavesurfer.value.on("seek", () => {
            currentTime.value = wavesurfer.value.getCurrentTime()
        })

        wavesurfer.value.on("audioprocess", () => {
            currentTime.value = wavesurfer.value.getCurrentTime()
        })

        wavesurfer.value.on("play", function () {
            isPlaying.value = true
        })

        wavesurfer.value.on("pause", function () {
            isPlaying.value = false
        })

        wavesurfer.value.container.addEventListener("contextmenu", (e) => {
            e.preventDefault()
        })
    })

    return {
        wavesurfer,
        duration,
        currentTime,
        wave,
        progressCanvas,
        isPlaying,
        isMuted,
        loadAudio,
        play,
        pause,
        playPause,
        drawRegion,
        drawImmutableRegion,
        mute,
        unmute,
        skip,
        skipBackward
    }
}

export function formatTime(timeInSeconds) {
    return [
        ("00" + Math.floor(timeInSeconds / 60 / 60)).slice(-2), // hours
        ("00" + Math.floor((timeInSeconds % 3600) / 60)).slice(-2), // minutes
        ("00" + Math.floor(timeInSeconds % 60)).slice(-2), // seconds
    ].join(":")
}

export function denormalizeTime(formattedTime) {
    const [hours, minutes, seconds] = formattedTime.split(":")

    return hours * 60 * 60 + minutes * 60 + parseInt(seconds)
}

const fakePeaks = [0, 0, 0, 0, -3, 2, -48, 49, -47, 47, -40, 40, -39, 33, -41, 35, -32, 34, -35, 35, -31, 34, -34, 40, -32, 36, -29, 29, -27, 27, -47, 47, -49, 48, -46, 47, -30, 27, -38, 39, -37, 41, -31, 42, -35, 45, -31, 34, -32, 37, -26, 41, -26, 40, -27, 40, -49, 49, -47, 48, -39, 34, -44, 31, -44, 39, -45, 31, -44, 36, -47, 31, -39, 34, -40, 29, -37, 28, -34, 26, -39, 44, -50, 49, -46, 46, -24, 30, -35, 41, -40, 45, -36, 40, -35, 38, -34, 38, -31, 33, -32, 34, -29, 31, -26, 21, -33, 30, -54, 63, -54, 64, -43, 53, -25, 34, -18, 22, -8, 8, -31, 27, -33, 48, -36, 45, -41, 48, -41, 43, -13, 21, -64, 61, -48, 49, -26, 37, -24, 43, -28, 35, -33, 33, -33, 29, -16, 17, -9, 8, -4, 2, -11, 11, -11, 11, -12, 11, -12, 12, -11, 11, -9, 4, -6, 8, -6, 9, -6, 7, -35, 31, -63, 63, -59, 50, -14, 8, -49, 31, -55, 48, -38, 39, -29, 23, -6, 6, -62, 61, -64, 54, -62, 48, -53, 38, -68, 43, -51, 34, -12, 10, -3, 2, -19, 12, -23, 18, -63, 55, -64, 57, -64, 47, -61, 47, -49, 30, -49, 45, -43, 46, -46, 63, -63, 57, -51, 52, -35, 42, -53, 63, -43, 41, -34, 49, -38, 42, -44, 36, -44, 32, -26, 32, -21, 31, -11, 8, -5, 4, -7, 7, -30, 19, -36, 27, -53, 37, -37, 41, -50, 43, -49, 43, -36, 40, -19, 9, -27, 19, -37, 27, -37, 50, -44, 51, -24, 23, -39, 45, -42, 45, -64, 55, -54, 39, -27, 36, -50, 35, -28, 30, -29, 27, -64, 48, -64, 34, -23, 21, -22, 18, -31, 20, -49, 34, -21, 17, -11, 11, -12, 12, -16, 25, -25, 24, -25, 16, -25, 16, -20, 18, -61, 47, -63, 55, -11, 10, -44, 45, -53, 52, -11, 15, -45, 33, -50, 50, -39, 35, -26, 27, -30, 18, -41, 50, -39, 26, -48, 33, -27, 31, -23, 23, -39, 40, -38, 41, -36, 41, -35, 51, -43, 46, -40, 50, -30, 49, -22, 33, -8, 11, -28, 14, -50, 45, -43, 47, -19, 14, -19, 13, -36, 37, -38, 52, -49, 46, -37, 26, -41, 35, -56, 40, -54, 41, -39, 34, -30, 23, -16, 14, -7, 6, -32, 23, -35, 25, -40, 34, -51, 46, -44, 46, -36, 12, -48, 53, -38, 40, -39, 54, -36, 42, -43, 37, -13, 14, -32, 39, -38, 36, -30, 32, -47, 28, -63, 44, -50, 33, -7, 6, -13, 10, -18, 12, -63, 56, -54, 63, -33, 32, -35, 40, -58, 48, -49, 49, -35, 43, -32, 49, -49, 53, -58, 56, -64, 58, -64, 55, -64, 40, -48, 31, -20, 17, -11, 11, -10, 9, -9, 8, -9, 7, -10, 7, -12, 11, -9, 11, -4, 5, -9, 11, -9, 10, -11, 11, -11, 12, -11, 12, -11, 12, -9, 11, -9, 10, -9, 9, -11, 12, -12, 11, -11, 11, -5, 6, -7, 10, -8, 11, -6, 8, -4, 4, -3, 3, -1, 1, -2, 2, -1, 1, -3, 2, -27, 33, -55, 35, -65, 54, -64, 46, -35, 20, -38, 29, -37, 23, -27, 27, -30, 31, -42, 46, -40, 46, -28, 13, -62, 65, -61, 64, -55, 61, -35, 39, -51, 41, -42, 34, -44, 30, -52, 29, -13, 12, -63, 42, -51, 35, -41, 32, -36, 33, -45, 36, -46, 30, -31, 21, -30, 19, -45, 41, -34, 30, -22, 17, -30, 21, -35, 24, -32, 21, -3, 2, -1, 1, -2, 3, -63, 47, -64, 49, -60, 47, -45, 45, -45, 48, -32, 37, -44, 50, -45, 41, -44, 24, -46, 31, -40, 22, -47, 33, -33, 21, -32, 42, -40, 49, -32, 35, -42, 61, -52, 49, -48, 44, -31, 33, -9, 19, -20, 21, -40, 37, -49, 57, -62, 54, -52, 41, -41, 40, -38, 47, -37, 35, -62, 51, -59, 40, -46, 32, -32, 27, -34, 34, -31, 32, -22, 18, -37, 33, -30, 29, -12, 13, -58, 57, -56, 60, -23, 34, -18, 12, -32, 23, -29, 22, -20, 23, -12, 12, -8, 11, -5, 6, -5, 6, -11, 12, -12, 12, -11, 11, -6, 7, -9, 6, -9, 7, -9, 6, -10, 7, -10, 7, -11, 9, -55, 44, -59, 44, -57, 55, -42, 49, -45, 43, -29, 23, -47, 39, -17, 15, -12, 8, -42, 48, -23, 9, -32, 37, -63, 44, -46, 26, -42, 27, -64, 50, -55, 59, -63, 56, -63, 50, -38, 46, -37, 49, -49, 40, -35, 31, -34, 38, -22, 12, -42, 27, -41, 48, -64, 56, -51, 54, -38, 32, -33, 29, -29, 20, -25, 31, -37, 33, -38, 32, -16, 12, -26, 20, -21, 21, -36, 32, -32, 28, -37, 41, -45, 48, -33, 36, -47, 18, -10, 8, -64, 50, -65, 51, -63, 64, -63, 64, -53, 63, -47, 48, -24, 21, -18, 20, -39, 35, -49, 31, -63, 47, -56, 41, -43, 36, -50, 33, -40, 34, -22, 22, -27, 38, -27, 30, -38, 31, -38, 34, -62, 43, -53, 51, -32, 20, -11, 11, -45, 34, -49, 34, -33, 26, -30, 27, -33, 28, -36, 28, -30, 28, -22, 19, -18, 12, -10, 8, -13, 12, -10, 10, -5, 5, -9, 10, -8, 11, -11, 11, -12, 11, -11, 12, -11, 11, -10, 11, -9, 9, -23, 18, -39, 25, -13, 13, -24, 22, -35, 33, -11, 11, -64, 40, -64, 45, -57, 42, -64, 45, -47, 49, -51, 54, -21, 12, -24, 35, -21, 26, -23, 15, -32, 34, -26, 28, -38, 39, -52, 39, -21, 28, -34, 22, -64, 46, -61, 43, -12, 11, -12, 11, -53, 60, -57, 62, -32, 42, -27, 21, -48, 50, -44, 59, -43, 49, -58, 51, -55, 47, -30, 30, -37, 23, -46, 28, -26, 33, -39, 30, -46, 31, -17, 21, -33, 38, -35, 38, -15, 12, -64, 58, -63, 59, -47, 45, -45, 48, -31, 27, -36, 27, -32, 30, -37, 28, -40, 35, -31, 35, -19, 19, -11, 13, -8, 7, -1, 1, -1, 1, -1, 1, 0, 0, 0, 0, 0, 0, -7, 7, -12, 12, -9, 9, -5, 6, -5, 5, -6, 5, -19, 20, -64, 37, -63, 36, -38, 30, -8, 7, -41, 41, -63, 47, -63, 60, -63, 64, -55, 53, -39, 24, -39, 47, -50, 52, -53, 39, -58, 47, -21, 19, -21, 29, -17, 21, -3, 2, -47, 54, -57, 49, -63, 44, -44, 31, -54, 31, -51, 43, -33, 38, -22, 23, -16, 18, -12, 12, -11, 10, -3, 4, -1, 1, -9, 9, -12, 12, -11, 11, -7, 6, -8, 6, -9, 6, -9, 6, -57, 48, -63, 58, -63, 50, -36, 32, -27, 26, -20, 15, -12, 12, -21, 24, -61, 63, -61, 62, -36, 47, -63, 44, -40, 30, -41, 45, -32, 38, -35, 37, -44, 37, -46, 46, -43, 42, -33, 30, -22, 23, -32, 31, -37, 33, -8, 7, -26, 29, -19, 12, -37, 46, -58, 47, -28, 21, -43, 29, -46, 46, -29, 31, -19, 22, -30, 27, -20, 18, -16, 16, -12, 11, -2, 2, -2, 2, -7, 8, -1, 1, -6, 7, -7, 6, -6, 6, -12, 12, -12, 12, -9, 10, -7, 8, -9, 10, -10, 9, -9, 10, -41, 48, -65, 62, -65, 50, -21, 15, -29, 28, -50, 38, -54, 50, -45, 36, -51, 32, -42, 44, -56, 48, -42, 31, -64, 43, -63, 44, -55, 55, -11, 11, -38, 20, -50, 60, -42, 35, -58, 40, -33, 28, -9, 11, -43, 39, -54, 43, -20, 19, -17, 15, -44, 38, -51, 39, -39, 28, -30, 28, -35, 24, -23, 25, -17, 15, -19, 23, -48, 38, -58, 28, -21, 13, -54, 39, -63, 47, -44, 37, -43, 35, -32, 30, -40, 28, -21, 25, -30, 35, -30, 32, -47, 34, -33, 30, -21, 20, -16, 14, -32, 36, -32, 34, -32, 34, -40, 35, -53, 42, -49, 38, -40, 26, -22, 24, -32, 35, -26, 27, -15, 11, -44, 50, -39, 38, -25, 30, -27, 35, -35, 47, -45, 52, -24, 38, -18, 24, -12, 12, -12, 11, -11, 11, -6, 9, -8, 9, -7, 7, -10, 10, -9, 10, -5, 6, -3, 3, -2, 2, -2, 2, -11, 11, -11, 12, -8, 7, -2, 3, -8, 8, -8, 8, -4, 5, -8, 10, -10, 6, -1, 1, -2, 4, -9, 11, -64, 52, -64, 36, -64, 36, -43, 40, -64, 62, -51, 51, -19, 28, -22, 29, -17, 12, -24, 21, -21, 21, -27, 38, -35, 54, -42, 60, -33, 52, -16, 18, -7, 6, -5, 4, -5, 4, -2, 2, -7, 7, -17, 21, -61, 59, -64, 46, -54, 62, -43, 53, -56, 62, -44, 58, -36, 54, -20, 28, -19, 16, -22, 23, -26, 28, -26, 32, -33, 46, -39, 42, -32, 43, -15, 19, -11, 8, -12, 12, -11, 11, -6, 7, -7, 9, -11, 12, -27, 43, -64, 62, -64, 63, -63, 58, -63, 59, -33, 32, -23, 20, -16, 13, -36, 27, -35, 32, -33, 30, -22, 15, -45, 60, -63, 60, -38, 57, -26, 27, -11, 10, -10, 8, -10, 9, -9, 8, -31, 35, -64, 43, -64, 35, -49, 40, -47, 42, -53, 51, -18, 26, -18, 21, -21, 22, -29, 20, -64, 49, -61, 46, -53, 59, -38, 31, -40, 31, -26, 17, -29, 27, -30, 20, -16, 9, -3, 4, -9, 9, -6, 6, -6, 9, -63, 61, -64, 35, -61, 38, -28, 27, -64, 52, -64, 50, -20, 37, -27, 30, -21, 19, -36, 18, -39, 30, -48, 31, -49, 31, -51, 36, -63, 55, -62, 51, -62, 52, -12, 15, -7, 7, -51, 19, -65, 46, -62, 64, -64, 65, -64, 58, -64, 61, -64, 53, -64, 52, -63, 58, -66, 59, -65, 53, -66, 47, -65, 54, -64, 64, -64, 62, -64, 64, -64, 63, -65, 63, -65, 59, -63, 55, -65, 60, -54, 64, -64, 63, -64, 62, -64, 48, -63, 59, -64, 64, -64, 61, -56, 63, -61, 64, -64, 61, -64, 62, -63, 41, -23, 20, -12, 12, -11, 13, -5, 5, -8, 7, -8, 10, -8, 9, -11, 12, -12, 11, -12, 11, -10, 11, -9, 10, -9, 7, -3, 3, -3, 4, -3, 2, -1, 1, -1, 1, -1, 1, -1, 1, -1, 1, -1, 1, -1, 1, -1, 0, 0, 0, 0, 1, 0, 1, 0, 0, -1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
